import React from 'react';
import { clientSearchKeys } from 'utils/clientSearch';
import {
    DateRow,
    IconAndTitle,
    TextRow,
    TypeRow,
    FullNameRow,
    TagComponent,
    StatusCard,
    FilterTags,
    NavigateCalendar,
    SimpleTooltip,
} from 'components';
import {clientStatusEnums, Images, PermissionList, renderClientName, RolePermission, tagEnums} from 'utils';
import {canReadTags} from 'store/tags/tag.utils';
import {Eligibility} from '../../fragments/schedule/core/common';

const AUTH_EXPIRING_DAYS = 14;

const getAuthEndPillStyle = (item) => {
    if (!item?.contractDates?.to || item.authHasFutureReplacement) return null;
    if (item.authDaysRemaining != null) {
        if (item.authDaysRemaining < 0) return {background: '#FEF3F2', color: '#B42318'};
        if (item.authDaysRemaining <= AUTH_EXPIRING_DAYS) return {background: '#FFFAEB', color: '#B54708'};
    }
    return null;
};

const clientStatusesEnum = {
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE',
    FBA: 'FBA',
    ON_HOLD: 'ON_HOLD',
    WAIT_LIST: 'WAIT_LIST',
    TERMINATED: 'TERMINATED',
    GRADUATED: 'GRADUATED',
    INVITED: 'INVITED',
};

/**
 * Client statuses offered by the Status column filter.
 *
 * Exported rather than inlined in the head so the SelectedFilters chip bar can label a selection
 * from the same list the column offered it from. The column prepends its own `All` option; that is
 * a control, not a value, so it stays out of here.
 */
export const clientStatusFilterList = [
    {name: 'Active', id: clientStatusesEnum.ACTIVE},
    {name: 'Inactive', id: clientStatusesEnum.INACTIVE},
    {name: 'FBA', id: clientStatusesEnum.FBA},
    {name: 'On Hold', id: clientStatusesEnum.ON_HOLD},
    {name: 'Waitlist', id: clientStatusesEnum.WAIT_LIST},
    {name: 'Terminated', id: clientStatusesEnum.TERMINATED},
    {name: 'Graduated', id: clientStatusesEnum.GRADUATED},
    {name: 'Intake', id: clientStatusesEnum.INVITED},
];

export const defaultStatuses = [
    clientStatusesEnum.ACTIVE,
    clientStatusesEnum.FBA,
    clientStatusesEnum.ON_HOLD,
    clientStatusesEnum.WAIT_LIST,
    clientStatusesEnum.INVITED,
    // clientStatusesEnum.GRADUATED,
];

// Local wrapper so `FilterTags` is dereferenced at RENDER time, not at module-load time.
// This file sits in a circular barrel graph (components <-> fragments <-> pages); building
// `<FilterTags />` inline in the top-level array below read the not-yet-initialized
// `components` barrel export during a cyclic import and crashed with
// "Cannot read properties of undefined (reading 'FilterTags')".
const FilterTagsCell = () => <FilterTags/>;

// A function, not a top-level array, so the tag permission is read at RENDER time. Permissions are
// written to storage by an async saga after login; anything evaluated at module-load time captures
// an empty snapshot and silently drops the column until a full page reload (see the note in
// utils/hooks/rolePermissions.js).
/** Who may see the Payer column — and so sort or filter the list by it (the API enforces the same). */
export const canReadClientPayers = () =>
    RolePermission([PermissionList.CLIENT_ENROLLMENT_MANAGE?.code, PermissionList.CLIENT_ENROLLMENT_READ?.code]);

/** The Payer filter also needs the payer list itself; without it the column still sorts. */
export const canFilterClientsByPayer = () => canReadClientPayers() && RolePermission([PermissionList.FS_LIST_READ?.code]);


export const clientHead = (payerOptions) => [
    {name: 'code', title: 'Code', searchKey: clientSearchKeys.CODE, width: '120px'},
    {name: 'fullName', title: 'Full Name', searchKey: clientSearchKeys.NAME},
    {name: 'gender', title: 'Gender', disabled: true, custom: false, width: '100px'},
    {name: '', title: 'DOB', icon: 'date', disabled: true, custom: false},
    {name: '', title: 'Intake Date', disabled: true, custom: false},

    RolePermission([PermissionList.CLIENT_ENROLLMENT_MANAGE?.code, PermissionList.CLIENT_ENROLLMENT_READ?.code]) &&
    {
        name: 'payer',
        title: 'Payer',
        disabled: true,
        sortable: true,
        filterTitle: 'Filter by Payer',
        listWidth: 240,
        listMaxHeight: 320,
        filterList: canFilterClientsByPayer() ? [{name: 'All', id: 'All'}, ...(payerOptions || [])] : undefined,
    },
    RolePermission([PermissionList.CLIENT_AUTHORIZATION_READ?.code, PermissionList.CLIENT_AUTHORIZATION_MANAGE?.code]) &&
    {name: 'contractDateStart', title: 'Auth. Start', disabled: true, iconType: 'date'},
    RolePermission([PermissionList.CLIENT_AUTHORIZATION_READ?.code, PermissionList.CLIENT_AUTHORIZATION_MANAGE?.code]) &&
    {name: 'contractDateEnd', title: 'Auth. End', disabled: true, iconType: 'date'},
    {name: '', title: 'Address', custom: false},
    canReadTags() && {name: '', title: 'Tags', custom: false, renderView: <FilterTagsCell/>},
    {
        name: 'statuses',
        title: 'Status',
        disabled: true,
        width: '140px',
        multiple: true,
        filterList: [{name: 'All', id: 'All'}, ...clientStatusFilterList],
        defaultSelected: defaultStatuses,
    },
    {name: '', title: 'Action', custom: false, width: '60px'},
];

// const renderStatus = (item) => {
//     return getValueDisplay(item?.status, 'Status');
// };

export const renderBorderColor = (item) => {
    const color =
        item?.status === 'ACTIVE'
            ? '#6FD231'
            : item?.status === 'FBS'
                ? '#ADD8E6'
                : item?.status === 'FBA'
                    ? '#ADD8E6'
                    : item?.status === 'ON_HOLD'
                        ? '#FFCA33'
                        : item?.status === 'WAIT_LIST'
                            ? '#FE7B43'
                            : item?.status === 'TERMINATED'
                                ? '#FE7070'
                                : item?.status === 'GRADUATED'
                                    ? '#51439E'
                                    : 'grey';

    return {
        borderLeft: `8px solid ${color}`,
        borderRadius: '8px 0 0 8px',
    };
};

export const clientBody = (requestParams, handleAction) => [
    {
        rowText: (item) => <IconAndTitle icon={Images.clients} title={item?.code ? item?.code : '--'} textWidth={7}/>,
    },
    {
        rowText: (item) => (
            <TextRow
                name={renderClientName({
                    firstName: item?.firstName,
                    lastName: item?.lastName,
                    middleName: item?.middleName,
                })}
                textWidth={7}
            />
        ),
    },
    {rowText: (item) => (item?.gender ? <TypeRow text={item?.gender}/> : '--')},
    {rowText: (item) => <DateRow date={item?.birthday}/>},
    {rowText: (item) => <DateRow date={item?.registrationDate ? item.registrationDate : ''}/>},

    RolePermission([PermissionList.CLIENT_ENROLLMENT_MANAGE?.code, PermissionList.CLIENT_ENROLLMENT_READ?.code]) &&
    {
        rowText: (item) => (
            <div className="flex-align-center" style={{gap: 8}}>
                {item?.eligibilityCheck && <Eligibility info={item} payerName={item?.enrollment}/>}
                <TextRow name={item?.enrollment ? item.enrollment : '--'} textWidth={10}/>
            </div>
        ),
    },
    RolePermission([PermissionList.CLIENT_AUTHORIZATION_READ?.code, PermissionList.CLIENT_AUTHORIZATION_MANAGE?.code]) &&
    {
        rowText: (item) => {
            return <DateRow date={item?.contractDates?.from}/>;
        },
    },
    RolePermission([PermissionList.CLIENT_AUTHORIZATION_READ?.code, PermissionList.CLIENT_AUTHORIZATION_MANAGE?.code]) &&
    {
        rowText: (item) => {
            const pill = getAuthEndPillStyle(item);
            if (!pill) return <DateRow date={item?.contractDates?.to}/>;
            return (
                <span
                    style={{
                        background: pill.background,
                        color: pill.color,
                        padding: '2px 6px',
                        borderRadius: '16px',
                        fontSize: '14px',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                    }}
                >
                    <DateRow date={item?.contractDates?.to}/>
                </span>
            );
        },
    },
    {
        rowText: (item) => <div>
            {item?.address?.formattedAddress ?
                <TextRow
                    name={`${item?.address?.zip ? `${item?.address?.zip},` : ''} ${item?.address?.city}`}
                    toolText={item?.address?.formattedAddress}
                />
                :
                '--'
            }
        </div>
    },
    canReadTags() && {
        rowText: (item) => <TagComponent userInfo={item} module={tagEnums.CLIENT} requestParams={requestParams}/>,
        notClickable: true,
    },
    // { rowText: (item) => <StatusRow status={item?.status} /> },
    {
        rowText: (item) => (
            <div className="flex-align-center" style={{gap: 8}}>
                <StatusCard status={item?.status}/>

                {item?.status === clientStatusEnums.INVITED && !item?.inviteCompleted && (
                    <SimpleTooltip title={'Resend intake form'} placement="top" arrow>
                        <button
                            className="reset-btn-by-default"
                            onClick={(e) => {
                                e?.preventDefault();
                                e?.stopPropagation();
                                handleAction(item, 'resendInvite');
                            }}
                        >
                            <img
                                style={{
                                    filter: 'brightness(0) saturate(100%) invert(32%) sepia(73%) saturate(5950%) hue-rotate(210deg) brightness(94%) contrast(91%)',
                                }}
                                src={Images.arrowUpRight}
                                alt="icon"
                            />
                        </button>
                    </SimpleTooltip>
                )}
                {item?.status === clientStatusEnums.INVITED && item?.inviteCompleted && (
                    <SimpleTooltip title={'Preview and edit client intake info'} placement="top" arrow>
                        <button
                            className="reset-btn-by-default"
                            onClick={(e) => {
                                e?.preventDefault();
                                e?.stopPropagation();
                                handleAction(item, 'completeInvite');
                            }}
                        >
                            <img
                                style={{
                                    filter: 'brightness(0) saturate(100%) invert(32%) sepia(73%) saturate(5950%) hue-rotate(210deg) brightness(94%) contrast(91%)',
                                }}
                                src={Images.showAyeBlue}
                                alt="icon"
                            />
                        </button>
                    </SimpleTooltip>
                )}
            </div>
        ),
        notClickable: true,
    },
    {rowText: (item) => <NavigateCalendar client={item}/>, notClickable: true},
];

export const ACTION_TYPE = 'GET_CLIENTS';

export const clientNotYet = {
    title: 'No Clients Added Yet',
    subTitle: 'Start by adding your first client to manage their information and services.',
    image: Images.noClientYet,
};

export const clientExpirationEnums = {
    AUTH_EXPIRED: 'AUTH_EXPIRED',
    EXPIRING_SOON: 'EXPIRING_SOON'
}